import React, { useEffect, useRef } from "react";
import "../../styles/components/About.css";
import {
  FaUser,
  FaBriefcase,
  FaCode,
  FaRocket,
  FaFutbol,
} from "react-icons/fa";
import { useLanguage } from "../../hooks/LanguageContext";

const skills = [
  "JavaScript",
  "Java",
  "React",
  "Node.js",
  "Docker",
  "Git",
  "SQL",
  "Firebase",
  "UX/UI",
];

const translations = {
  es: {
    knowMe: "Conóceme",
    knowMeContent: (
      <>
        Soy <span className="highlight">Miguel Olteanu</span>, una persona cuyo
        objetivo es mejorar y aprender todos los días, tanto en el ámbito
        profesional como personal.
        <br />
        <br />
        Me considero una{" "}
        <span className="highlight">persona dedicada</span> a sus objetivos y{" "}
        <span className="highlight">perseverante</span> hasta alcanzarlos. Esa
        determinación me ha llevado a enfrentar desafíos con confianza y a no
        rendirme ante las adversidades, ya sea en proyectos personales o en mi
        desarrollo profesional.
        <br />
        <br />
        Mi enfoque es siempre mantener una{" "}
        <span className="highlight">actitud positiva</span> y valorar cada
        experiencia que la vida me ofrece.
      </>
    ),
    professionalPath: "Trayectoria Profesional",
    professionalPathContent: (
      <>
        Actualmente trabajo como{" "}
        <span className="highlight">Ingeniero de Software</span> en Procesia, aplicando mis
        conocimientos en productos tecnológicos reales y entornos profesionales
        exigentes.
        <br />
        <br />
        Antes de dar el salto al entorno profesional, construí una base sólida
        mediante <span className="highlight">proyectos propios</span> de
        desarrollo web y móvil, gestión de bases de datos e interfaces
        cuidadas, que hoy siguen formando parte de mi forma de trabajar.
      </>
    ),
    technicalSkills: "Habilidades Técnicas",
    technicalSkillsContent: (
      <>
        A lo largo de mi experiencia —en el ámbito profesional, en la
        universidad y por cuenta propia— he consolidado habilidades en
        tecnologías como:
      </>
    ),
    hobbies: "Hobbies e Intereses",
    hobbiesContent: (
      <>
        Además de mi carrera como ingeniero de software, el{" "}
        <span className="highlight">deporte</span> es fundamental en mi vida.
        <br />
        <br />
        Soy un gran aficionado al{" "}
        <span className="highlight">fútbol</span>, un deporte que disfruto
        tanto como espectador como jugador desde que tengo uso de razón. También
        me gusta realizar <span className="highlight">ejercicio</span>{" "}
        diariamente, lo cual no solo me ayuda a mantener una vida saludable,
        sino que también mejora notablemente mi estado de ánimo. Además, me
        encanta ver <span className="highlight">series</span> y pasar tiempo
        jugando y paseando con mis{" "}
        <span className="highlight">perros</span>.
        <br />
        <br />
        Estas actividades son esenciales para mí: me ayudan a desconectar y
        aportan equilibrio a mi vida.
      </>
    ),
    nextSteps: "Próximos pasos",
    nextStepsContent: (
      <>
        Mi foco ahora es crecer profesionalmente y profundizar en desarrollo de
        software de calidad, con especial interés en{" "}
        <span className="highlight">web y móvil con React</span>.
        <br />
        <br />
        En paralelo continúo con{" "}
        <span className="highlight">proyectos personales</span> que me permiten
        experimentar, aprender y mantener viva la curiosidad.
        <br />
        <br />
        El objetivo principal sigue siendo el mismo: aprendizaje continuo y
        mejora constante, a nivel personal y profesional.
      </>
    ),
    viewCV: "Ver CV",
  },

  en: {
    knowMe: "Get to Know Me",
    knowMeContent: (
      <>
        I am <span className="highlight">Miguel Olteanu</span>, someone whose
        goal is to improve and learn every day, both professionally and
        personally.
        <br />
        <br />I consider myself a{" "}
        <span className="highlight">dedicated person</span> toward my goals and{" "}
        <span className="highlight">perseverant</span> until I achieve them.
        That determination has helped me face challenges with confidence and
        not give up in the face of adversity — in personal projects or in my
        professional growth.
        <br />
        <br />
        My approach is always to keep a{" "}
        <span className="highlight">positive attitude</span> and value every
        experience life offers me.
      </>
    ),
    professionalPath: "Professional Path",
    professionalPathContent: (
      <>
        I currently work as a{" "}
        <span className="highlight">Software Engineer</span> at Procesia, applying my skills
        to real technology products in demanding professional environments.
        <br />
        <br />
        Before joining the professional world, I built a strong foundation
        through <span className="highlight">personal projects</span> in web and
        mobile development, databases, and polished interfaces — habits that
        still shape how I work today.
      </>
    ),
    technicalSkills: "Technical Skills",
    technicalSkillsContent: (
      <>
        Across my experience — professionally, at university, and on my own — I
        have strengthened skills in technologies such as:
      </>
    ),
    hobbies: "Hobbies and Interests",
    hobbiesContent: (
      <>
        Beyond my career as a software engineer,{" "}
        <span className="highlight">sports</span> are fundamental in my life.
        <br />
        <br />I am a big fan of{" "}
        <span className="highlight">football</span>, a sport I have enjoyed
        both as a spectator and as a player for as long as I can remember. I
        also like to <span className="highlight">exercise</span> daily, which
        helps me stay healthy and notably improves my mood. Additionally, I
        love watching <span className="highlight">TV series</span> and spending
        time with my <span className="highlight">dogs</span>.
        <br />
        <br />
        These activities help me disconnect and bring balance to my life.
      </>
    ),
    nextSteps: "Next Steps",
    nextStepsContent: (
      <>
        My focus now is to grow professionally and deepen my craft in quality
        software — with a special interest in{" "}
        <span className="highlight">web and mobile with React</span>.
        <br />
        <br />
        In parallel I keep building{" "}
        <span className="highlight">personal projects</span> that let me
        experiment, learn, and stay curious.
        <br />
        <br />
        The main goal remains the same: continuous learning and steady
        improvement, personally and professionally.
      </>
    ),
    viewCV: "View CV",
  },
};

function About() {
  const { isEnglish } = useLanguage();
  const lang = isEnglish ? "en" : "es";
  const sectionRefs = useRef([]);

  const cvLinks = {
    es: "/CV_MIGUEL_OLTEANU.pdf",
    en: "/CV_Miguel_Olteanu_EN.pdf",
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.12 }
    );

    const refs = sectionRefs.current.filter(Boolean);
    refs.forEach((ref) => observer.observe(ref));

    return () => observer.disconnect();
  }, []);

  const sections = [
    {
      title: translations[lang].knowMe,
      content: translations[lang].knowMeContent,
      icon: <FaUser />,
    },
    {
      title: translations[lang].professionalPath,
      content: translations[lang].professionalPathContent,
      icon: <FaBriefcase />,
    },
    {
      title: translations[lang].technicalSkills,
      content: translations[lang].technicalSkillsContent,
      icon: <FaCode />,
      showSkills: true,
    },
    {
      title: translations[lang].hobbies,
      content: translations[lang].hobbiesContent,
      icon: <FaFutbol />,
    },
    {
      title: translations[lang].nextSteps,
      content: translations[lang].nextStepsContent,
      icon: <FaRocket />,
    },
  ];

  return (
    <div className="about-container page-atmosphere">
      <div className="about-intro">
        <p className="about-kicker">
          {isEnglish ? "Software Engineer" : "Ingeniero de Software"}
        </p>
      </div>

      {sections.map((section, index) => (
        <section
          key={section.title}
          className={`about-section ${index % 2 === 0 ? "left" : "right"}`}
          ref={(el) => {
            sectionRefs.current[index] = el;
          }}
        >
          <div className="content-card">
            <h2 className="about-title">{section.title}</h2>
            <div className="about-description">{section.content}</div>
            {section.showSkills && (
              <ul className="skills-grid">
                {skills.map((skill) => (
                  <li key={skill} className="skill-chip">
                    {skill}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="icon-container" aria-hidden="true">
            {section.icon}
          </div>
        </section>
      ))}

      <a
        href={cvLinks[lang]}
        target="_blank"
        className="cv-button-fixed"
        rel="noopener noreferrer"
      >
        <span className="cv-button-content">{translations[lang].viewCV}</span>
      </a>
    </div>
  );
}

export default About;
